import { ErrorMessage } from 'components/Widgets/ErrorMessage';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import useLocation from 'hooks/useLocation';
import { useState } from 'react';
import { getApiErrorMessage } from 'services/_infra/apiError.utils';
import { useGetAllActiveAlertsQuery } from 'services/alerts/alerts.api';
import {
  WeatherMapStaleDataNotice,
  WeatherMapUpdatingStatus,
} from '../../components/WeatherMapDataStatus/WeatherMapDataStatus';
import WeatherMapSegmentedControl from '../../components/WeatherMapSegmentedControl/WeatherMapSegmentedControl';
import '../../features/SevereWeatherAlerts/assets/severeWeatherAlerts.scss';
import SevereWeatherAlertAreaSelect from '../../features/SevereWeatherAlerts/components/SevereWeatherAlertAreaSelect/SevereWeatherAlertAreaSelect';
import SevereWeatherAlertMapContent from '../../features/SevereWeatherAlerts/components/SevereWeatherAlertMapContent/SevereWeatherAlertMapContent';
import SevereWeatherAlertPanel from '../../features/SevereWeatherAlerts/components/SevereWeatherAlertPanel/SevereWeatherAlertPanel';
import {
  SEVERE_WEATHER_ALERTS_QUERY_OPTIONS,
  SEVERE_WEATHER_ALERTS_REFRESH_INTERVAL_MINUTES,
  SEVERE_WEATHER_ALERTS_ROUTE,
} from '../../features/SevereWeatherAlerts/config/severeWeatherAlerts.config';
import useSevereWeatherAlertSelection from '../../features/SevereWeatherAlerts/hooks/useSevereWeatherAlertSelection';
import { resolveWeatherMapWidgetLayout } from '../../utils/weatherMapWidgetLayout.utils';
import WeatherMapWidgetEmptyState from '../components/WeatherMapWidgetEmptyState/WeatherMapWidgetEmptyState';
import WeatherMapWidgetLayout from '../components/WeatherMapWidgetLayout/WeatherMapWidgetLayout';

const SevereWeatherAlertsWidget = ({ layout, dimensions, containerSizePx }) => {
  const { locations } = useLocation();
  const [compactView, setCompactView] = useState('map');
  const {
    data: alerts = [],
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  } = useGetAllActiveAlertsQuery(undefined, SEVERE_WEATHER_ALERTS_QUERY_OPTIONS);
  const {
    alertAreas,
    selectedArea,
    selectedAlert,
    selectedAlertId,
    selectArea,
    selectAreaById,
    selectAlert,
  } = useSevereWeatherAlertSelection(alerts);
  const resolvedLayout = resolveWeatherMapWidgetLayout({
    layout,
    containerWidthPx: containerSizePx?.width ?? null,
  });
  const isCompactSinglePanel = resolvedLayout === '1x1';
  const visibleAlertCount = selectedArea?.alerts?.length ?? alerts.length;

  if (isLoading) {
    return <WidgetLoadingAnimation widgetName="SevereWeatherAlerts" dimensions={dimensions} />;
  }

  if (isError && !alerts.length) {
    return <ErrorMessage message={getApiErrorMessage(error)} onRetry={refetch} />;
  }

  if (!alertAreas.allAlertAreas.length) {
    return (
      <WeatherMapWidgetEmptyState
        ariaLabel="Severe weather warnings widget"
        title="Severe Weather Warnings"
        message={
          alerts.length
            ? 'Active alerts are available, but none include a map-ready boundary.'
            : 'No active severe weather alerts are available right now.'
        }
        className="severe-weather-alerts severe-weather-alerts-widget severe-weather-alert-widget-status"
      />
    );
  }

  const mapContent = (
    <SevereWeatherAlertMapContent
      areas={alertAreas.allAlertAreas}
      selectedArea={selectedArea}
      fallbackCenter={locations.effectiveLocation?.coords}
      onSelectArea={selectArea}
      scrollZoom
    />
  );
  const informationContent = (
    <SevereWeatherAlertPanel
      areas={alertAreas.allAlertAreas}
      selectedArea={selectedArea}
      selectedAlert={selectedAlert}
      selectedAlertId={selectedAlertId}
      onSelectArea={selectAreaById}
      onSelectAlert={selectAlert}
    />
  );
  const compactViewOptions = [
    { value: 'map', label: 'Map' },
    { value: 'alerts', label: `Alerts (${visibleAlertCount})` },
  ];

  return (
    <section
      aria-label="Severe weather warnings widget"
      className="severe-weather-alerts severe-weather-alerts-widget d-flex h-100 min-h-0 w-100 flex-column gap-1 overflow-hidden"
    >
      <WidgetTimeUpdate
        realtime
        route={SEVERE_WEATHER_ALERTS_ROUTE}
        updateIntervalBasis={SEVERE_WEATHER_ALERTS_REFRESH_INTERVAL_MINUTES}
        compact
      />

      {isError && (
        <WeatherMapStaleDataNotice
          compact
          message="Update failed. Showing the most recently available alerts."
        />
      )}

      {isCompactSinglePanel ? (
        <div className="d-flex min-h-0 flex-grow-1 flex-column gap-2 overflow-hidden">
          <WeatherMapSegmentedControl
            ariaLabel="Severe weather widget view"
            options={compactViewOptions}
            value={compactView}
            onChange={setCompactView}
          />

          {compactView === 'map' ? (
            <>
              <SevereWeatherAlertAreaSelect
                areas={alertAreas.allAlertAreas}
                selectedAreaId={selectedArea?.id}
                onSelectArea={selectAreaById}
                wrapperClassName="min-w-0 flex-shrink-0"
                label={null}
                ariaLabel="Alert area"
              />
              <section
                aria-label="Severe weather alerts map"
                className="d-flex min-h-0 flex-grow-1"
              >
                {mapContent}
              </section>
            </>
          ) : (
            <section
              aria-label="Severe weather alert information"
              className="d-flex min-h-0 flex-grow-1 overflow-hidden rounded-2 border"
            >
              {informationContent}
            </section>
          )}
        </div>
      ) : (
        <WeatherMapWidgetLayout
          layout={resolvedLayout}
          mapAriaLabel="Severe weather alerts map"
          informationAriaLabel="Severe weather alert information"
          mapClassName={
            resolvedLayout === '1x2' ? 'severe-weather-alert-widget-map-stacked' : undefined
          }
          informationClassName="severe-weather-alert-widget-information overflow-hidden"
          mapContent={mapContent}
          informationContent={informationContent}
        />
      )}

      {isFetching && !isLoading && (
        <WeatherMapUpdatingStatus message="Updating severe weather alerts" />
      )}
    </section>
  );
};

export default SevereWeatherAlertsWidget;
